import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MdDoubleArrow } from "react-icons/md";
import { WiTime2 } from "react-icons/wi";
import { useDispatch, useSelector } from "react-redux";
import { listSurveys, listLiveSurveys, fetchProviderSurveys } from "../redux/slice/surveySlice";
import DashboardHeader from "./components/DashboardHeader";
import DashboardTabbar from "./components/DashboardTabbar";

const SurveyPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);

  const assignedSurveys = useSelector((state) => state.assignedSurveys);
  const profileSurveysData = assignedSurveys?.surveys?.data || [];
  const liveSurveysData = assignedSurveys?.liveSurveys?.data || [];
  const isLoading = assignedSurveys?.surveyLoading || assignedSurveys?.liveSurveysLoading;

  // Provider surveys
  const providerSurveysRaw = assignedSurveys?.providerSurveys?.data?.surveys || [];
  const providerSurveysLoading = assignedSurveys?.providerSurveysLoading;

  useEffect(() => {
    window.scrollTo(0, 0);
    const user = JSON.parse(localStorage.getItem("user"));
    const id = user?.id || "";
    setUserId(id);
  }, []);

  const getUserCountryCode = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.data?.countryCode || user?.countryCode || "";
  };

  useEffect(() => {
    dispatch(listSurveys());
    dispatch(listLiveSurveys());
    dispatch(fetchProviderSurveys());
  }, [dispatch]);

  // Transform and merge surveys
  const mergedSurveys = useMemo(() => {
    const userCountryCode = getUserCountryCode();

    const profileSurveys = profileSurveysData.map((item) => ({
      id: item._id,
      type: "profile",
      name: item.title || item.name,
      loi: item.loi,
      status: item.status,
      reward: item.reward?.amount,
      currency: item.reward?.currency === "INR" ? "₹" : item.reward?.currency || "₹",
      endDate: item.endDate,
      startUrl: null,
    }));

    const liveSurveys = liveSurveysData
      .map((item) => {
        const matchingCountry = item.countries?.find(
          (c) => c.country?.toUpperCase() === userCountryCode?.toUpperCase()
        );
        if (!matchingCountry) return null;

        return {
          id: item._id,
          surveyId: item.surveyId,
          type: "live",
          name: item.basic?.name,
          loi: item.basic?.loi,
          status: item.status,
          reward: matchingCountry.vendorCpi,
          currency: matchingCountry.vendorCurrency === "INR" ? "₹" : matchingCountry.vendorCurrency || "₹",
          endDate: item.basic?.endDate,
          startUrl: matchingCountry.startUrl,
        };
      })
      .filter(Boolean);

    return [...profileSurveys, ...liveSurveys];
  }, [profileSurveysData, liveSurveysData]);

  // Transform provider surveys
  const providerSurveys = useMemo(() => {
    return providerSurveysRaw.map((item) => ({
      id: item.id || item.survey?.id,
      type: "provider",
      name: item.survey?.ui_title || "",
      description: item.survey?.ui_description || "",
      icon: item.survey?.ui_icon || "",
      loi: item.survey?.loi,
      status: item.survey?.status || item.response_status,
      reward: item.survey?.respondent_cpi,
      currency: item.survey?.respondent_currency || "WS Points",
      startDate: item.survey?.start_date,
      endDate: item.survey?.end_date,
      startUrl: item.survey?.url,
    }));
  }, [providerSurveysRaw]);

  const openSurvey = (survey) => {
    if ((survey.type === "live" || survey.type === "provider") && survey.startUrl) {
      const finalUrl = survey.startUrl.replace("{{uid}}", userId || "");
      window.open(finalUrl, "_blank");
    } else {
      navigate(`/survey/${survey.id}`);
    }
  };

  const allLoading = isLoading || providerSurveysLoading;

  return (
    <div>
      <DashboardHeader />
      <div className="container mt-4 min-h">
        <DashboardTabbar />

        {/* Provider Surveys Section */}
        <div className="site-card-wrapper">
          <h2 className="h3 mb-4">Available Surveys</h2>

          {providerSurveysLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading surveys...</p>
            </div>
          ) : providerSurveys.length === 0 ? (
            <div className="text-center py-4">
              <p>No surveys available at the moment.</p>
            </div>
          ) : (
            <div className="cards-container row row-cols-2 row-cols-sm-2 row-cols-md-4 row-cols-lg-3">
              {providerSurveys.map((item, index) => (
                <div className="col mb-4" key={`provider-${item.id}-${index}`}>
                  <div
                    className="survey-card feedback-survey-card"
                    onClick={() => openSurvey(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="feedback-data">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="loi">
                          <WiTime2 /> {item.loi} min
                        </span>
                        <span className="loi">{item.status}</span>
                      </div>
                      <p>Earn : {item.reward} {item.currency}</p>
                      <p>Expire - {item.endDate}</p>
                      <span className="start-btn">
                        <MdDoubleArrow />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Profile & Live Surveys Section */}
        {/* {mergedSurveys.length > 0 && (
          <div className="site-card-wrapper mt-4">
            <h2 className="h3 mb-4">Profile Surveys</h2>
            <div className="cards-container row row-cols-2 row-cols-sm-2 row-cols-md-4 row-cols-lg-3">
              {mergedSurveys.map((item, index) => (
                <div className="col mb-4" key={`${item.type}-${item.id}-${index}`}>
                  <div
                    className="survey-card feedback-survey-card"
                    onClick={() => openSurvey(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="feedback-data">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="loi">
                          <WiTime2 /> {item.loi} min
                        </span>
                        <span className="loi">Status: {item.status}</span>
                      </div>
                      <p>Earn : {item.reward}</p>
                      <p>Expire Date - {item.endDate?.split("T")[0]}</p>
                      <span className="start-btn">
                        <MdDoubleArrow />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default SurveyPage;
